import type { ListChrome } from './listPrefs';

/**
 * 목록 스켈레톤 — 데이터가 오기 전에 **틀과 뼈대를 먼저** 그린다(2026-08-22 UX 채택안 #3).
 *
 * 지금까지는 서버가 다 준비될 때까지 흰 화면이었다. `loading.tsx` 가 이걸 그리는 동안
 * Next 가 본문을 스트리밍한다 — 헤더·푸터(레이아웃)는 이미 떠 있으므로 목록 자리만 뼈대다.
 *
 * 서버 컴포넌트다(상태 없음). 카드 수는 목록 기본값(12)과 비슷하게 8개 — 접히는 화면에서
 * 뼈대가 실제보다 길어 보이지 않게 한 줄 두 줄 사이로 잡았다.
 */
export function ListSkeleton({ cards = 8, chrome }: { cards?: number; chrome: ListChrome }) {
  /*
   * 보기(카드/리스트)에 맞춰 뼈대를 그린다(2026-08-28 사용자 지시). 전에는 늘 카드였고,
   * 리스트로 보는 분은 카드 뼈대가 깔렸다가 리스트로 바뀌었다 — 2026-08-23 에 잡았던
   * "카드형이었다가 깜박이며 리스트형으로" 와 같은 축의 문제가 첫 로딩에만 남아 있었다.
   * 판정은 `skeletonChrome()` 이 **쿠키만 읽어** 한다(뼈대 하나에 API 를 더 부르지 않는다).
   *
   * 제목 아래 줄은 **실물이 그리는 것과 같은 수**여야 한다(2026-08-31 사용자 제보로 고침).
   * 전에는 44px 짜리 한 줄만 그려서, 실물(탭 46 · 분류 64 · 검색 62 · 정렬 32 + 간격)이
   * 들어오는 순간 목록이 200px 남짓 아래로 밀렸다 — 뼈대가 자리를 안 잡아 준 셈이다.
   * 높이는 `web.css` 의 `.sk-row-*` 가 실물 CSS(`tab-primary`·`cat-filter`·
   * `bbs-search-bar`·`sort-tab`)의 안쪽 여백에서 계산해 갖고 있다.
   */
  const list = chrome.view === 'list';
  return (
    <main>
      <div className="wrap py30">
        <div className="cp-page-header mb20">
          <span className="sk sk-title" />
        </div>
        {chrome.typeTab && <div className="sk-row sk-row-tabs mb15" />}
        {chrome.catTab && <div className="sk-row sk-row-cat mb15" />}
        {/* 검색 바는 설정과 무관하게 늘 있다(`CourseFilters`) */}
        <div className="sk-row sk-row-search mb15" />
        {chrome.sortRow && <div className="sk sk-row-sort mb15" />}
        {/* 실물과 **같은 컨테이너 클래스**를 쓴다 — 간격이 다르면 뼈대에서 실물로 바뀔 때 튄다 */}
        <div className={list ? 'course-list mb20' : 'course-grid mb20'}>
          {list ? <SkeletonRows n={cards} /> : <SkeletonCards n={cards} />}
        </div>
      </div>
    </main>
  );
}

/**
 * 카드 뼈대 — **틀 없이 낱개로** 낸다.
 *
 * 첫 화면(`loading.tsx`)뿐 아니라 **더보기로 이어 받을 때도** 쓰기 위해서다
 * (2026-08-26 사용자 제안). 그때는 이미 그려진 `course-grid` 안에 이어 붙여야 하므로
 * 이쪽이 틀을 만들면 격자가 두 겹이 된다.
 */
export function SkeletonCards({ n }: { n: number }) {
  return (
    <>
      {Array.from({ length: n }, (_, i) => (
        <div className="course-card" key={i} aria-hidden>
          <div className="sk sk-thumb" />
          <div className="course-body">
            <span className="sk sk-line" />
            <span className="sk sk-line short" />
            <span className="sk sk-price" />
          </div>
        </div>
      ))}
    </>
  );
}

/** 리스트 줄 뼈대 — 카드와 짝. 썸네일이 왼쪽에 붙고 값이 오른쪽에 온다 */
export function SkeletonRows({ n }: { n: number }) {
  return (
    <>
      {Array.from({ length: n }, (_, i) => (
        <div className="clist-item" key={i} aria-hidden>
          <div className="sk clist-thumb" />
          <div className="clist-info">
            <span className="sk sk-line" />
            <span className="sk sk-line short" />
          </div>
          <div className="clist-actions">
            <span className="sk sk-price" />
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * 상세 뼈대 — **목록 뼈대가 상세에 뜨는 것을 막는다**(2026-08-26 사용자 지적).
 *
 * Next 의 `loading.tsx` 는 **하위 경로까지 덮는다** — `/lectures/loading.tsx` 가
 * `/lectures/123` 에도 쓰여서, 상세로 들어가는데 카드 격자가 깔렸다.
 * `[id]` 자리에 자기 뼈대를 두면 그 자리만 이걸 쓴다.
 *
 * 모양은 가이드 `lecture-detail.html` 의 위쪽(`pd-top`) 그대로다 — 왼쪽 큰 그림,
 * 오른쪽 제목·값. 아래 탭은 그리지 않는다(무엇이 올지 화면마다 다르다).
 */
export function DetailSkeleton() {
  return (
    <main>
      <div className="wrap py30">
        <div className="pd-crumb mb20"><span className="sk sk-line" style={{ width: 220 }} /></div>
        <div className="pd-top">
          <div className="pd-thumb"><span className="sk sk-thumb" style={{ height: '100%' }} /></div>
          <div className="pd-info">
            <span className="sk sk-line" style={{ width: '35%' }} />
            <span className="sk sk-title" style={{ width: '80%', marginTop: 10 }} />
            <span className="sk sk-line short" style={{ marginTop: 16 }} />
            <span className="sk sk-line short" />
            <span className="sk sk-price" style={{ width: '45%', height: 28 }} />
          </div>
        </div>
      </div>
    </main>
  );
}
